/*
 * WHAT THE TOP BAR SAYS ABOUT THE PAGE (components/AppTopBar.js).
 *
 * The page's title and subtitle live in the top bar, not on the page: a page says them once
 * (PageHeader does it for every page that uses it) and the bar shows them, with the path
 * under them. A page can add steps below its sidebar item to that path, e.g. the domain page:
 *   XWatch › Domains › xeoai.com › Uptime ▾        (the last one opens the page's tabs)
 *
 *   usePublishTopBar({ title, subtitle, back: { label, onClick }, trail: [{ label, onClick?, menu? }] })
 *     menu: [{ label, onClick, active }]
 *
 * Outside the provider (tests, a page rendered on its own) nothing is published and
 * PageHeader draws its own title as before.
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

const TopBarContext = createContext(null);

export function TopBarProvider({ children }) {
  const [page, setPage] = useState(null);
  const value = useMemo(() => ({ page, setPage }), [page]);
  return <TopBarContext.Provider value={value}>{children}</TopBarContext.Provider>;
}

/** True inside the app shell, where the top bar shows the page's title. */
export const useInTopBar = () => !!useContext(TopBarContext);

/** What the current page published (null = nothing: the bar falls back to the sidebar's names). */
export const useTopBarPage = () => useContext(TopBarContext)?.page || null;

/** Words of a title that may be a node, so a change of words (not of object identity) republishes. */
const words = (v) => {
  if (v == null || typeof v === 'boolean') return '';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (Array.isArray(v)) return v.map(words).join('');
  return v.props ? words(v.props.children) : '';
};

let seq = 0;
export function usePublishTopBar(config) {
  const ctx = useContext(TopBarContext);
  const set = ctx?.setPage;
  const latest = useRef(config);
  latest.current = config;
  const idRef = useRef(null);
  if (idRef.current === null) { seq += 1; idRef.current = seq; }
  // Handlers go through `latest`, so the published copy never holds a stale closure, and a
  // re-render with the same words doesn't publish again (no render loop through the provider).
  const trail = config?.trail || [];
  const key = JSON.stringify({
    t: words(config?.title), s: words(config?.subtitle), b: config?.back ? config.back.label || 'Back' : '',
    tr: trail.map((c) => [c.label, !!c.onClick, (c.menu || []).map((m) => [m.label, !!m.active])]),
  });
  useEffect(() => {
    if (!set || !config) return undefined;
    const id = idRef.current;
    const c = latest.current;
    set({
      id,
      title: c.title,
      subtitle: c.subtitle,
      back: c.back ? { label: c.back.label || 'Back', onClick: () => latest.current.back?.onClick?.() } : null,
      trail: (c.trail || []).map((step, i) => ({
        label: step.label,
        onClick: step.onClick ? () => latest.current.trail?.[i]?.onClick?.() : null,
        menu: step.menu ? step.menu.map((m, j) => ({ label: m.label, active: !!m.active, onClick: () => latest.current.trail?.[i]?.menu?.[j]?.onClick?.() })) : null,
      })),
    });
    return undefined;
  }, [set, key]); // eslint-disable-line react-hooks/exhaustive-deps
  // Leaving the page takes its words with it (unless a newer page has already published).
  useEffect(() => () => { if (set) set((p) => (p && p.id === idRef.current ? null : p)); }, [set]);
}
